import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { logout } from 'slice/userSlice';
import { useDispatch } from 'react-redux';
import { IoPersonCircleSharp } from "react-icons/io5";
import { FaUsers } from "react-icons/fa";
import { MdDashboard } from "react-icons/md";
import { RiMedicineBottleLine, RiVideoLine, RiLayoutMasonryLine } from "react-icons/ri";
import { toast } from 'react-toastify';
import { io } from "socket.io-client";
import { getRequest } from 'apis/ApiInstence';
import logo from '../asserts/img/logo.png'

const socket = io(process.env.REACT_APP_BACKEND);

const LS_CONTACT = 'contactus_last_seen';
const LS_EXPERT = 'expertcalling_last_seen';

const Layout = ({ children }) => {
  const [toggled, setToggle] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [contactUnread, setContactUnread] = useState(0);
  const [expertUnread, setExpertUnread] = useState(0);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isActive = (path) => pathname === path || pathname.startsWith(path + '/');
  const isExactActive = (path) => pathname === path;
  const isSegmentActive = (segment) => pathname.includes(segment);

  const adminName = localStorage.getItem('adminName') || 'Admin';

  const audioRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const [contactRes, expertRes] = await Promise.all([
        getRequest('contactus?limit=500', getAuthHeaders()),
        getRequest('expertCalling?limit=500&include_inactive=true', getAuthHeaders()),
      ]);

      const contactLastSeen = localStorage.getItem(LS_CONTACT);
      const expertLastSeen = localStorage.getItem(LS_EXPERT);

      if (contactRes.data.success) {
        const items = Array.isArray(contactRes.data.data) ? contactRes.data.data : [];
        const count = contactLastSeen
          ? items.filter((item) => new Date(item.createdAt) > new Date(contactLastSeen)).length
          : items.length;
        setContactUnread(count);
      }

      if (expertRes.data.success) {
        const items = Array.isArray(expertRes.data.data) ? expertRes.data.data : [];
        const count = expertLastSeen
          ? items.filter((item) => new Date(item.createdAt) > new Date(expertLastSeen)).length
          : items.length;
        setExpertUnread(count);
      }
    } catch {
      // silent fail
    }
  }, []);

  const markContactSeen = () => {
    localStorage.setItem(LS_CONTACT, new Date().toISOString());
    setContactUnread(0);
  };

  const markExpertSeen = () => {
    localStorage.setItem(LS_EXPERT, new Date().toISOString());
    setExpertUnread(0);
  };

  const handleTabClick = (tabName) => {
    setOpenMenu(openMenu === tabName ? null : tabName);
  };

  useEffect(() => {
    socket.on("new-payment", () => {
      toast('New Order Added');
      if (audioRef.current) {
        audioRef.current.play().catch(console.error);
      }
    });
    return () => {
      socket.off("new-payment");
    };
  }, []);

  useEffect(() => {
    if (pathname.includes('projects')) setOpenMenu('products');
    else if (pathname.includes('packages')) setOpenMenu('packages');
    else if (pathname.includes('contactus')) setOpenMenu('contactus');
    else if (pathname.includes('expertcalling')) setOpenMenu('expertcalling');
    else if (pathname.includes('banner')) setOpenMenu('banner');
    else if (pathname.includes('blogs')) setOpenMenu('blogs');
    else if (pathname.includes('testimonials')) setOpenMenu('testimonials');
    else if (pathname.includes('sitesetup')) setOpenMenu('sitesetup');
  }, [pathname]);

  useEffect(() => {
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts]);

  const logoutHandler = async () => {
    localStorage.removeItem('userToken');
    sessionStorage.removeItem('userToken');
    dispatch(logout());
    navigate('/signin');
    toast.success('Logged out successfully');
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <div id="wrapper">
      <ul className={toggled ? 'navbar-nav sidebar sidebar-dark toggled' : 'navbar-nav sidebar sidebar-dark accordion'} id="accordionSidebar">
        <Link className="sidebar-brand d-flex align-items-center p-4 mb-2" to="/" style={{ textDecoration: 'none' }}>
          <div className="sidebar-brand-icon">
            <img src={logo} alt="Graaho" style={{ width: '200px', height: 'auto' }} />
          </div>
        </Link>

        <div className="sidebar-heading">MAIN MENU</div>
        <li className={`nav-item ${isActive('/dashboard') ? 'active' : ''}`}>
          <Link className="nav-link" to="/dashboard">
            <MdDashboard /> <span>Dashboard</span>
          </Link>
        </li>

        <div className="sidebar-heading">MANAGEMENT</div>
        
        {/* Projects Dropdown */}
        <li className={`nav-item ${isSegmentActive('projects') ? 'active' : ''}`}>
          <button
            type="button"
            className={`nav-link cursor-pointer w-100 border-0 bg-transparent text-left ${openMenu === 'products' ? '' : 'collapsed'}`}
            onClick={() => handleTabClick('products')}
          >
            <RiMedicineBottleLine /> <span>Projects</span>
          </button>
          <div className={`collapse ${openMenu === 'products' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link className={`collapse-item ${isExactActive('/projects') ? 'active' : ''}`} to="/projects">Project List</Link>
              <Link className={`collapse-item ${isExactActive('/projects/add') ? 'active' : ''}`} to="/projects/add">Add Project</Link>
              <Link className={`collapse-item ${isExactActive('/projects/content-update') ? 'active' : ''}`} to="/projects/content-update">Page Content</Link>
            </div>
          </div>
        </li>

        <li className={`nav-item ${isSegmentActive('packages') ? 'active' : ''}`}>
          <button type="button" className="nav-link w-100 border-0 bg-transparent text-left" onClick={() => handleTabClick('packages')}>
            <RiLayoutMasonryLine /> <span>Packages</span>
          </button>
          <div className={`collapse ${openMenu === 'packages' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link className="collapse-item" to="/packages">Packages</Link>
              <Link className="collapse-item" to="/packages/add">Add Package</Link>
            </div>
          </div>
        </li>

        <li className={`nav-item ${isSegmentActive('contactus') ? 'active' : ''}`}>
          <button type="button" className="nav-link w-100 border-0 bg-transparent text-left" onClick={() => handleTabClick('contactus')}>
            <FaUsers /> <span>Contact Us</span>
            {contactUnread > 0 && <span className="badge badge-danger ml-auto">{contactUnread}</span>}
          </button>
          <div className={`collapse ${openMenu === 'contactus' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link className="collapse-item" to="/contactus" onClick={markContactSeen}>All Contact Us</Link>
            </div>
          </div>
        </li>

        <li className={`nav-item ${isSegmentActive('expertcalling') ? 'active' : ''}`}>
          <button type="button" className="nav-link w-100 border-0 bg-transparent text-left" onClick={() => handleTabClick('expertcalling')}>
            <FaUsers /> <span>Expert Enquiry</span>
            {expertUnread > 0 && <span className="badge badge-danger ml-auto">{expertUnread}</span>}
          </button>
          <div className={`collapse ${openMenu === 'expertcalling' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link className="collapse-item" to="/expertcalling" onClick={markExpertSeen}>Expert Enquiries</Link>
            </div>
          </div>
        </li>

        <li className="nav-item">
          <Link className="nav-link" to="/live-video">
            <RiVideoLine /> <span>Live Videos</span>
          </Link>
        </li>

        <li className={`nav-item ${isSegmentActive('banner') ? 'active' : ''}`}>
          <button type="button" className="nav-link w-100 border-0 bg-transparent text-left" onClick={() => handleTabClick('banner')}>
            <RiLayoutMasonryLine /> <span>Banners</span>
          </button>
          <div className={`collapse ${openMenu === 'banner' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link className="collapse-item" to="/banner">Banner List</Link>
              <Link className="collapse-item" to="/banner/add">Add Banner</Link>
            </div>
          </div>
        </li>

        <li className={`nav-item ${isSegmentActive('blogs') ? 'active' : ''}`}>
          <button type="button" className="nav-link w-100 border-0 bg-transparent text-left" onClick={() => handleTabClick('blogs')}>
            <RiMedicineBottleLine /> <span>Blogs</span>
          </button>
          <div className={`collapse ${openMenu === 'blogs' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link className="collapse-item" to="/blogs">Blogs</Link>
              <Link className="collapse-item" to="/blogs/add">Add Blog</Link>
            </div>
          </div>
        </li>

        <li className={`nav-item ${isSegmentActive('testimonials') ? 'active' : ''}`}>
          <button type="button" className="nav-link w-100 border-0 bg-transparent text-left" onClick={() => handleTabClick('testimonials')}>
            <RiMedicineBottleLine /> <span>Testimonials</span>
          </button>
          <div className={`collapse ${openMenu === 'testimonials' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link className="collapse-item" to="/testimonials">Testimonials List</Link>
              <Link className="collapse-item" to="/testimonials/add">Add Testimonial</Link>
            </div>
          </div>
        </li>

        <li className={`nav-item ${isSegmentActive('sitesetup') ? 'active' : ''}`}>
          <button type="button" className="nav-link w-100 border-0 bg-transparent text-left" onClick={() => handleTabClick('sitesetup')}>
            <RiLayoutMasonryLine /> <span>Site Setup</span>
          </button>
          <div className={`collapse ${openMenu === 'sitesetup' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link className="collapse-item" to="/sitesetup">Site Setup List</Link>
              <Link className="collapse-item" to="/sitesetup/add">Add Site Setup</Link>
            </div>
          </div>
        </li>

        <div className="text-center d-none d-md-inline mt-auto py-4">
          <button 
            type="button" 
            className="rounded-circle border-0" 
            onClick={() => setToggle(!toggled)} 
            style={{ background: '#f1f5f9', color: 'var(--text-main)', width: '40px', height: '40px' }}
          >
            <i className={toggled ? "fa fa-chevron-right" : "fa fa-chevron-left"}></i>
          </button>
        </div>
      </ul>

      <div id="content-wrapper" className="d-flex flex-column" style={{ background: 'var(--bg-main)', flexGrow: 1 }}>
        <div id="content">
          <nav className="navbar navbar-expand topbar mb-4 static-top">
            <button 
              type="button"
              onClick={() => setToggle(!toggled)} 
              className="btn btn-link d-md-none rounded-circle mr-3" 
              style={{ color: 'var(--text-body)' }}
            >
              <i className="fa fa-bars"></i>
            </button>
            <div className="d-none d-md-block">
              <span className="text-muted small">Welcome Admin,</span>
              <h6 className="mb-0 font-weight-bold">{adminName}</h6>
            </div>
            <div className="flex-grow-1"></div>
            <ul className="navbar-nav align-items-center">
              <li className="nav-item dropdown no-arrow">
                <div 
                  className="dropdown-toggle d-flex align-items-center p-1 cursor-pointer" 
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  onKeyDown={(e) => handleKeyDown(e, () => setDropdownOpen(!dropdownOpen))}
                  role="button"
                  tabIndex={0}
                >
                  <div style={{ width: '38px', height: '38px', background: 'var(--primary)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IoPersonCircleSharp size={28} style={{ color: "white" }} />
                  </div>
                  <span className="ml-2 d-none d-lg-inline small font-weight-bold">{adminName}</span>
                </div>
                {dropdownOpen && (
                  <div className="dropdown-menu dropdown-menu-right shadow-lg show border-0" style={{ position: 'absolute', right: 0, top: '100%', marginTop: '0.75rem' }}>
                    <button className="dropdown-item text-danger" onClick={logoutHandler}>
                      <i className="fas fa-sign-out-alt mr-2"></i> Logout
                    </button>
                  </div>
                )}
              </li>
            </ul>
          </nav>
          <div className="px-4 pb-5">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
